import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Paper, 
  Typography,
  Button,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { 
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

interface LocationState {
  siteName: string;
  route: string;
}

function ShareGroupLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  
  const [copied, setCopied] = useState(false);
  const [state, setState] = useState<LocationState | null>(locationState);

  // Check multiple sources for site data
  React.useEffect(() => {
    if (!locationState?.siteName || !locationState?.route) {
      // Try URL query parameters first (for full page reload)
      const searchParams = new URLSearchParams(location.search);
      const siteName = searchParams.get('siteName');
      const route = searchParams.get('route');
      
      if (siteName && route) {
        setState({ siteName, route });
        return;
      }
      
      // Try sessionStorage
      const storedData = sessionStorage.getItem('shareGroupData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          setState(parsedData);
          // Clear after reading
          sessionStorage.removeItem('shareGroupData');
        } catch (err) {
          console.error('Error parsing stored data:', err);
          navigate('/');
        }
      } else {
        navigate('/');
      }
    }
  }, [locationState, location.search, navigate]);

  if (!state?.siteName || !state?.route) {
    return null;
  }

  const groupUrl = `https://matchpoint.hk/${state.route}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(groupUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(`加入我的群組「${state.siteName}」\n${groupUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: state.siteName,
          text: `加入我的群組「${state.siteName}」`,
          url: groupUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  const handleGoToGroup = () => {
    navigate(`/${state.route}`);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        mt: 8, 
        mb: 8,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center' 
      }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          {/* Success Icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main' }} />
          </Box>

          <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#000' }}>
            群組創建成功！
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            {state.siteName}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Group Link Display */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            分享您的群組連結
          </Typography>

          <Box sx={{ 
            bgcolor: 'grey.100', 
            p: 2, 
            borderRadius: 1, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                wordBreak: 'break-all',
                flex: 1,
                mr: 1,
                fontFamily: 'monospace'
              }}
            >
              {groupUrl}
            </Typography>
            <IconButton 
              onClick={handleCopy}
              color={copied ? 'success' : 'primary'}
              sx={{ flexShrink: 0 }}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </IconButton>
          </Box>

          {copied && (
            <Alert severity="success" sx={{ mb: 3 }}>
              連結已複製到剪貼簿
            </Alert>
          )}

          {/* Share Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={handleWhatsAppShare}
              sx={{ 
                color: '#25D366',
                borderColor: '#25D366',
                '&:hover': {
                  borderColor: '#25D366',
                  bgcolor: 'rgba(37, 211, 102, 0.04)'
                }
              }}
            >
              WhatsApp
            </Button>
            
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleNativeShare}
              >
                分享
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={handleGoToGroup}
            >
              前往群組管理
            </Button>
            
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/')}
            >
              返回首頁
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ShareGroupLink;
